import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Wrench, X, Search, Check, RotateCcw, Camera, Loader2, Plus, Trash2, User as UserIcon, Clock, MapPin, AlertTriangle, AlarmClock, Download, BellRing } from 'lucide-react';
import { listMaintenance, setMaintenanceDone, createMaintenance, deleteMaintenance, setMaintenanceReminder, exportMaintenance } from '../lib/api';

const MAX = 500;

function timeAgo(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch (e) { return ''; }
}

function AddForm({ properties, onDone, onCancel }) {
  const [propertyId, setPropertyId] = useState('');
  const [comment, setComment] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const fileRef = useRef(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return properties;
    return properties.filter((p) => p.name.toLowerCase().includes(s) || (p.address || '').toLowerCase().includes(s));
  }, [properties, q]);

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(f); setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!propertyId) { toast.error('Choose a property'); return; }
    if (!comment.trim()) { toast.error('Describe the issue'); return; }
    setSaving(true);
    try {
      const form = new FormData();
      form.append('propertyId', propertyId);
      form.append('comment', comment.trim());
      if (urgent) form.append('urgent', 'true');
      if (photo) form.append('file', photo);
      await createMaintenance(form);
      toast.success('Maintenance added');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 space-y-3" data-testid="maintenance-add-form">
      <div>
        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Property</label>
        <div className="relative mb-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search property…" className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm" style={{ fontSize: '16px' }} data-testid="maintenance-add-search" />
        </div>
        <div className="max-h-40 overflow-y-auto space-y-1 border border-slate-100 rounded-lg p-1">
          {filtered.map((p) => (
            <button key={p.id} type="button" onClick={() => setPropertyId(p.id)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm ${propertyId === p.id ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}
              data-testid={`maintenance-add-pick-${p.id}`}>
              {p.name}{p.city ? <span className="text-slate-400"> · {p.city}</span> : ''}
            </button>
          ))}
          {filtered.length === 0 && <p className="text-sm text-slate-400 px-3 py-2">No properties</p>}
        </div>
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, MAX))} rows={3} placeholder="Describe what needs fixing…"
        className="w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" style={{ fontSize: '16px' }} data-testid="maintenance-add-comment" />
      <div className="flex justify-end -mt-1"><span className="text-[11px] text-slate-400">{comment.length}/{MAX}</span></div>
      {preview ? (
        <div className="relative inline-block">
          <img src={preview} alt="attachment" className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
          <button type="button" onClick={() => { setPhoto(null); setPreview(null); if (fileRef.current) fileRef.current.value=''; }} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border shadow flex items-center justify-center"><X size={13} /></button>
        </div>
      ) : (
        <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600"><Camera size={15} /> Add photo (optional)</button>
      )}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhoto} />
      <button
        type="button"
        onClick={() => setUrgent((v) => !v)}
        className={`w-full h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 border transition-colors ${urgent ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-500'}`}
        data-testid="maintenance-add-urgent"
      >
        <AlertTriangle size={15} />
        {urgent ? 'Marked as urgent' : 'Mark as urgent'}
      </button>
      <div className="flex gap-2 pt-1">
        <button type="button" onClick={onCancel} className="flex-1 h-11 rounded-xl font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
        <button type="button" onClick={submit} disabled={saving} className="flex-1 h-11 rounded-xl font-semibold text-white flex items-center justify-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 disabled:opacity-50" data-testid="maintenance-add-submit">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add
        </button>
      </div>
    </div>
  );
}

// Full maintenance manager for the Properties page (admin + manager).
export default function MaintenanceModal({ open, onClose, properties, isAdmin, onChange }) {
  const [tab, setTab] = useState('open');
  const [city, setCity] = useState('all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ items: [], counts: { open: 0, done: 0 }, cities: [] });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [donePromptId, setDonePromptId] = useState(null);
  const [doneNote, setDoneNote] = useState('');
  const [doneLabor, setDoneLabor] = useState('');
  const [doneParts, setDoneParts] = useState('');
  const [reminderEditId, setReminderEditId] = useState(null);
  const [reminderVal, setReminderVal] = useState('');
  const [showExport, setShowExport] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listMaintenance({ status: tab, city, search: search || undefined });
      setData(res.data);
      onChange?.(res.data.counts.open);
    } catch (e) {
      toast.error('Could not load maintenance');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, city, search]);

  const confirmDone = async (item) => {
    setBusyId(item.id);
    try {
      await setMaintenanceDone(item.id, true, {
        resolutionNote: doneNote.trim() || undefined,
        laborCost: doneLabor !== '' ? doneLabor : undefined,
        partsCost: doneParts !== '' ? doneParts : undefined,
      });
      setDonePromptId(null); setDoneNote(''); setDoneLabor(''); setDoneParts('');
      await load();
    } catch (e) { toast.error('Could not update'); } finally { setBusyId(null); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (city && city !== 'all') params.city = city;
      if (exportFrom) params.date_from = exportFrom;
      if (exportTo) params.date_to = exportTo;
      const res = await exportMaintenance(params);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `maintenance_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
      setShowExport(false);
    } catch (e) {
      toast.error('Could not export');
    } finally { setExporting(false); }
  };

  const reopen = async (item) => {
    setBusyId(item.id);
    try { await setMaintenanceDone(item.id, false); await load(); }
    catch (e) { toast.error('Could not update'); } finally { setBusyId(null); }
  };

  const saveReminder = async (item, clear = false) => {
    setBusyId(item.id);
    try {
      const iso = clear ? null : (reminderVal ? new Date(reminderVal).toISOString() : null);
      if (!clear && !iso) { toast.error('Pick a date & time'); setBusyId(null); return; }
      await setMaintenanceReminder(item.id, iso);
      toast.success(clear ? 'Reminder removed' : 'Reminder set');
      setReminderEditId(null); setReminderVal('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save reminder'); } finally { setBusyId(null); }
  };

  const remove = async (item) => {
    setBusyId(item.id);
    try { await deleteMaintenance(item.id); await load(); } catch (e) { toast.error('Could not delete'); } finally { setBusyId(null); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-start justify-center md:p-4 md:pt-[3vh]"
          onClick={onClose} data-testid="maintenance-modal">
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            className="bg-white w-full md:max-w-2xl rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] md:h-[88vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center"><Wrench size={22} className="text-white" /></div>
                <div>
                  <h2 className="text-lg font-bold text-white">Maintenance</h2>
                  <p className="text-white/80 text-xs">{data.counts.open} to do · {data.counts.done} done</p>
                </div>
              </div>
              <button onClick={onClose} className="text-white/80 hover:text-white p-1" data-testid="maintenance-close"><X size={22} /></button>
            </div>

            {adding ? (
              <div className="overflow-y-auto">
                <AddForm properties={properties} onCancel={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />
              </div>
            ) : (
              <>
                {/* Controls */}
                <div className="p-4 border-b border-slate-100 flex-shrink-0 space-y-3">
                  <div className="flex gap-2">
                    <button onClick={() => setTab('open')} className={`flex-1 h-10 rounded-xl text-sm font-semibold ${tab === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`} data-testid="maintenance-tab-open">To do ({data.counts.open})</button>
                    <button onClick={() => setTab('done')} className={`flex-1 h-10 rounded-xl text-sm font-semibold ${tab === 'done' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid="maintenance-tab-done">Done ({data.counts.done})</button>
                    <button onClick={() => setAdding(true)} className="h-10 px-4 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-emerald-500 flex items-center gap-1.5" data-testid="maintenance-open-add"><Plus size={16} /> Add</button>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search flat…" className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm" style={{ fontSize: '16px' }} data-testid="maintenance-search" />
                    </div>
                    <select value={city} onChange={(e) => setCity(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" data-testid="maintenance-city">
                      <option value="all">All cities</option>
                      {data.cities.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button onClick={() => setShowExport((v) => !v)} className={`h-10 px-3 rounded-lg border flex items-center gap-1.5 text-sm font-semibold ${showExport ? 'border-emerald-300 bg-emerald-50 text-emerald-600' : 'border-slate-200 bg-white text-slate-500 hover:text-emerald-600 hover:border-emerald-200'}`} title="Export CSV" data-testid="maintenance-export-btn"><Download size={16} /><span className="hidden sm:inline">Export</span></button>
                  </div>
                  {showExport && (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 flex flex-wrap items-end gap-2" data-testid="maintenance-export-bar">
                      <div>
                        <label className="block text-[11px] font-bold text-emerald-700 mb-1">From</label>
                        <input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} className="h-10 px-2 rounded-lg border border-emerald-200 text-sm bg-white" style={{ fontSize: '16px' }} data-testid="maintenance-export-from" />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-emerald-700 mb-1">To</label>
                        <input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} className="h-10 px-2 rounded-lg border border-emerald-200 text-sm bg-white" style={{ fontSize: '16px' }} data-testid="maintenance-export-to" />
                      </div>
                      <button onClick={handleExport} disabled={exporting} className="h-10 px-4 rounded-lg text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 flex items-center gap-1.5 disabled:opacity-50" data-testid="maintenance-export-download">
                        {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Download CSV
                      </button>
                      <span className="text-[11px] text-emerald-600/80 w-full sm:w-auto">Uses the current city filter. Leave dates empty for all time.</span>
                    </div>
                  )}
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loading ? (
                    <div className="flex justify-center py-10"><Loader2 size={26} className="animate-spin text-amber-500" /></div>
                  ) : data.items.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-slate-50 flex items-center justify-center mb-3"><Wrench size={26} className="text-slate-300" /></div>
                      <p className="text-slate-500 text-sm">{tab === 'open' ? 'Nothing to fix right now 🎉' : 'No completed maintenance yet'}</p>
                    </div>
                  ) : data.items.map((item) => {
                    const done = item.status === 'done';
                    const isUrgent = item.urgent && !done;
                    return (
                      <div key={item.id} className={`rounded-2xl p-4 border ${done ? 'bg-emerald-50/60 border-emerald-100' : isUrgent ? 'bg-red-50/70 border-red-200' : 'bg-white border-slate-200'}`} data-testid={`maintenance-item-${item.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-bold text-slate-900 truncate">{item.propertyName}</p>
                              {isUrgent && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold uppercase tracking-wide" data-testid={`maintenance-urgent-badge-${item.id}`}>
                                  <AlertTriangle size={10} /> Urgent
                                </span>
                              )}
                            </div>
                            {(item.propertyAddress || item.city) && (
                              <p className="text-xs text-slate-500 flex items-center gap-1 truncate"><MapPin size={11} />{item.propertyAddress || item.city}</p>
                            )}
                          </div>
                          {isAdmin && (
                            <button onClick={() => remove(item)} disabled={busyId === item.id} className="text-slate-300 hover:text-red-500 p-1 flex-shrink-0" title="Delete" data-testid={`maintenance-delete-${item.id}`}><Trash2 size={16} /></button>
                          )}
                        </div>
                        <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{item.comment}</p>
                        {item.photoUrl && (
                          <a href={item.photoUrl} target="_blank" rel="noreferrer">
                            <img src={item.photoUrl} alt="maintenance" className="mt-2 w-24 h-24 object-cover rounded-lg border border-slate-200" />
                          </a>
                        )}
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-400">
                          <span className="inline-flex items-center gap-1"><UserIcon size={11} />{item.submittedByName}</span>
                          <span className="inline-flex items-center gap-1"><Clock size={11} />{timeAgo(item.createdAt)}</span>
                          {!done && item.reminderAt && !item.reminderSentAt && (
                            <span className="inline-flex items-center gap-1 text-blue-500 font-semibold" data-testid={`maintenance-reminder-chip-${item.id}`}><AlarmClock size={11} />Reminder {timeAgo(item.reminderAt)}</span>
                          )}
                          {!done && item.reminderSentAt && (
                            <span className="inline-flex items-center gap-1 text-slate-400 font-medium" data-testid={`maintenance-reminder-sent-${item.id}`}><BellRing size={11} />Reminder sent</span>
                          )}
                          {done && item.doneByName && <span className="inline-flex items-center gap-1 text-emerald-600 font-semibold"><Check size={11} />Done by {item.doneByName} · {timeAgo(item.doneAt)}</span>}
                        </div>
                        {done && item.resolutionNote && (
                          <p className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid={`maintenance-resolution-${item.id}`}>
                            <span className="font-semibold">Fixed:</span> {item.resolutionNote}
                          </p>
                        )}
                        {done && (item.laborCost != null || item.partsCost != null) && (
                          <div className="mt-2 flex flex-wrap gap-2 text-[11px]" data-testid={`maintenance-cost-${item.id}`}>
                            {item.laborCost != null && <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-600">Labour: {item.laborCost}</span>}
                            {item.partsCost != null && <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-600">Parts: {item.partsCost}</span>}
                            {item.totalCost != null && <span className="px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 font-semibold">Total: {item.totalCost}</span>}
                          </div>
                        )}

                        {/* Reminder editor */}
                        {reminderEditId === item.id && (
                          <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-3" data-testid={`maintenance-reminder-editor-${item.id}`}>
                            <label className="block text-xs font-bold text-blue-700 mb-1.5 flex items-center gap-1"><AlarmClock size={13} /> Remind me at</label>
                            <input type="datetime-local" value={reminderVal} onChange={(e) => setReminderVal(e.target.value)}
                              className="w-full h-10 px-3 rounded-lg border border-blue-200 text-sm bg-white" style={{ fontSize: '16px' }} data-testid={`maintenance-reminder-input-${item.id}`} />
                            <div className="flex gap-2 mt-2">
                              {item.reminderAt && (
                                <button onClick={() => saveReminder(item, true)} disabled={busyId === item.id} className="flex-1 h-9 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-500 hover:bg-slate-50">Remove</button>
                              )}
                              <button onClick={() => { setReminderEditId(null); setReminderVal(''); }} className="flex-1 h-9 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">Cancel</button>
                              <button onClick={() => saveReminder(item)} disabled={busyId === item.id} className="flex-1 h-9 rounded-lg text-sm font-semibold text-white bg-blue-500 hover:bg-blue-600 flex items-center justify-center gap-1" data-testid={`maintenance-reminder-save-${item.id}`}>
                                {busyId === item.id ? <Loader2 size={15} className="animate-spin" /> : 'Save'}
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Done note prompt */}
                        {donePromptId === item.id && (
                          <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-xl p-3" data-testid={`maintenance-done-prompt-${item.id}`}>
                            <label className="block text-xs font-bold text-emerald-700 mb-1.5">What did you fix? (optional)</label>
                            <textarea value={doneNote} onChange={(e) => setDoneNote(e.target.value.slice(0, 500))} rows={2}
                              placeholder="e.g. replaced the tap washer"
                              className="w-full resize-none rounded-lg border border-emerald-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400" style={{ fontSize: '16px' }} data-testid={`maintenance-done-note-${item.id}`} />
                            <div className="flex gap-2 mt-2">
                              <div className="flex-1">
                                <label className="block text-[11px] font-bold text-emerald-700 mb-1">Labour cost</label>
                                <input type="number" min="0" step="0.01" value={doneLabor} onChange={(e) => setDoneLabor(e.target.value)} placeholder="0.00" className="w-full h-10 px-3 rounded-lg border border-emerald-200 text-sm bg-white" style={{ fontSize: '16px' }} data-testid={`maintenance-done-labor-${item.id}`} />
                              </div>
                              <div className="flex-1">
                                <label className="block text-[11px] font-bold text-emerald-700 mb-1">Parts cost</label>
                                <input type="number" min="0" step="0.01" value={doneParts} onChange={(e) => setDoneParts(e.target.value)} placeholder="0.00" className="w-full h-10 px-3 rounded-lg border border-emerald-200 text-sm bg-white" style={{ fontSize: '16px' }} data-testid={`maintenance-done-parts-${item.id}`} />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-2">
                              <button onClick={() => { setDonePromptId(null); setDoneNote(''); setDoneLabor(''); setDoneParts(''); }} className="flex-1 h-9 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">Cancel</button>
                              <button onClick={() => confirmDone(item)} disabled={busyId === item.id} className="flex-1 h-9 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 flex items-center justify-center gap-1.5" data-testid={`maintenance-done-confirm-${item.id}`}>
                                {busyId === item.id ? <Loader2 size={15} className="animate-spin" /> : <><Check size={15} /> Confirm done</>}
                              </button>
                            </div>
                          </div>
                        )}

                        {donePromptId !== item.id && reminderEditId !== item.id && (
                          <div className="flex gap-2 mt-3">
                            {!done && (
                              <button
                                onClick={() => {
                                  setReminderEditId(item.id); setDonePromptId(null);
                                  if (item.reminderAt && !item.reminderSentAt) {
                                    const d = new Date(item.reminderAt);
                                    setReminderVal(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
                                  } else { setReminderVal(''); }
                                }}
                                disabled={busyId === item.id}
                                className={`h-10 px-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 border ${item.reminderAt && !item.reminderSentAt ? 'border-blue-200 bg-blue-50 text-blue-600' : 'border-slate-200 bg-white text-slate-500 hover:text-blue-600 hover:border-blue-200'}`}
                                title="Set a reminder"
                                data-testid={`maintenance-reminder-btn-${item.id}`}>
                                <AlarmClock size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => { if (done) { reopen(item); } else { setDonePromptId(item.id); setDoneNote(''); setReminderEditId(null); } }}
                              disabled={busyId === item.id}
                              className={`flex-1 h-10 rounded-xl text-sm font-semibold flex items-center justify-center gap-1.5 ${done ? 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50' : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:opacity-90'} disabled:opacity-50`}
                              data-testid={`maintenance-done-${item.id}`}>
                              {busyId === item.id ? <Loader2 size={16} className="animate-spin" /> : done ? <><RotateCcw size={15} /> Reopen</> : <><Check size={16} /> Mark done</>}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
